import React, { useState } from 'react';
import {
    Container,
    Card,
    CardBody,
    Button,
    Navbar,
    NavbarBrand,
    Nav,
    NavItem
} from 'reactstrap';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import {
    FaArrowLeft,
    FaHome,
    FaUserCircle,
    FaSignOutAlt,
    FaBars,
    FaTimes,
    FaChevronDown,
    FaChevronRight
} from 'react-icons/fa';
import Swal from 'sweetalert2';
import { authActions } from '../sagas/authSlice';
import logo from '../assets/images/logoh.png';
import './ModuleLayout.css';

const ModuleLayout = ({ children, moduleConfig, breadcrumbs }) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const { fullName, businessName } = useSelector(state => state.auth);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const [openSubMenu, setOpenSubMenu] = useState(null);

    const handleLogout = () => {
        Swal.fire({
            title: t('auth.logoutConfirmTitle'),
            text: t('auth.logoutConfirmText'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#dc3545',
            cancelButtonColor: '#6c757d',
            confirmButtonText: t('common.confirm'),
            cancelButtonText: t('common.cancel')
        }).then((result) => {
            if (result.isConfirmed) {
                dispatch(authActions.logout());
                Swal.fire({
                    icon: 'success',
                    title: t('auth.logoutSuccess'),
                    showConfirmButton: false,
                    timer: 1500
                }).then(() => {
                    navigate('/login');
                });
            }
        });
    };

    const handleBackToModules = () => {
        localStorage.removeItem('selectedModule');
        navigate('/modules');
    };

    return (
        <div className="module-layout-wrapper">
            {/* Enhanced Top Navigation Bar */}
            <div className="module-header-wrapper">
                <Navbar className="module-navbar compact-navbar" expand="lg">
                    <Container fluid className="px-3">
                        {/* ===== TOP ROW ===== */}
                        <div className="w-100 d-flex align-items-center justify-content-between">
                            {/* Left */}
                            <div className="d-flex align-items-center">
                                <Button
                                    color="link"
                                    className="sidebar-toggle-btn me-2"
                                    onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
                                >
                                    {sidebarCollapsed ? <FaBars size={16} /> : <FaTimes size={16} />}
                                </Button>

                                <NavbarBrand className="d-flex align-items-center p-0">
                                    <img
                                        src={logo}
                                        alt="MatjarPos"
                                        height="32"
                                        className="me-2"
                                    />
                                    <div className="brand-info">
                                        <div className="business-name small fw-bold">{businessName}</div>
                                        <div className="module-indicator d-flex align-items-center">
                                            <moduleConfig.icon
                                                size={13}
                                                className="me-1"
                                                style={{ color: moduleConfig?.color }}
                                            />
                                            <span className="module-name small text-muted">
                                                {moduleConfig?.title}
                                            </span>
                                        </div>
                                    </div>
                                </NavbarBrand>
                            </div>

                            {/* Right */}
                            <div className="d-flex align-items-center">
                                <div className="d-none d-md-flex align-items-center me-3">
                                    <FaUserCircle size={20} className="text-muted me-2" />
                                    <span className="small fw-semibold">{fullName}</span>
                                </div>

                                <Button
                                    color="danger"
                                    size="sm"
                                    className="logout-btn px-2 py-1"
                                    onClick={handleLogout}
                                >
                                    <FaSignOutAlt size={12} />
                                </Button>
                            </div>
                        </div>

                        {/* ===== BREADCRUMB ROW ===== */}
                        <div className="w-100 d-flex align-items-center justify-content-between mt-1 breadcrumb-inline">
                            <nav className="breadcrumb-nav small">
                                <span className="breadcrumb-item" onClick={handleBackToModules}>
                                    Modules
                                </span>
                                <span className="breadcrumb-separator">›</span>
                                <span className="breadcrumb-item" onClick={() => navigate('')}>
                                    {moduleConfig?.title}
                                </span>

                                {breadcrumbs?.map((breadcrumb, index) => (
                                    <React.Fragment key={index}>
                                        <span className="breadcrumb-separator">›</span>
                                        {breadcrumb.href ? (
                                            <span
                                                className="breadcrumb-item"
                                                onClick={() => navigate(breadcrumb.href)}
                                            >
                                                {breadcrumb.label}
                                            </span>
                                        ) : (
                                            <span className="breadcrumb-current">
                                                {breadcrumb.label}
                                            </span>
                                        )}
                                    </React.Fragment>
                                ))}
                            </nav>

                            <small className="text-muted d-none d-lg-block">
                                {new Date().toLocaleDateString('fr-FR', {
                                    weekday: 'short',
                                    day: '2-digit',
                                    month: 'short',
                                    year: 'numeric'
                                })}
                            </small>
                        </div>
                    </Container>
                </Navbar>
            </div>


            <div className="module-content-wrapper d-flex">
                {/* Sidebar Navigation */}
                <aside className={`module-sidebar bg-white border-end ${sidebarCollapsed ? 'collapsed' : ''}`}>
                    <div className="sidebar-header p-3 border-bottom">
                        <div className="d-flex align-items-center justify-content-between">
                            <div className="d-flex align-items-center">
                                <moduleConfig.icon
                                    size={24}
                                    style={{ color: moduleConfig.color }}
                                    className="me-2"
                                />
                                {!sidebarCollapsed && (
                                    <div>
                                        <h6 className="mb-0 fw-bold">{moduleConfig.title}</h6>
                                        <small className="text-muted">{moduleConfig.description}</small>
                                    </div>
                                )}
                            </div>
                            {!sidebarCollapsed && (
                                <Button
                                    color="link"
                                    size="sm"
                                    className="p-0 text-muted"
                                    onClick={() => setSidebarCollapsed(true)}
                                >
                                    <FaTimes size={16} />
                                </Button>
                            )}
                        </div>
                    </div>

                    <div className="sidebar-menu p-2">
                        {/* Back to Modules Button */}
                        <Card className="mb-2 border-0">
                            <CardBody
                                className="p-2 cursor-pointer d-flex align-items-center"
                                onClick={handleBackToModules}
                                style={{ cursor: 'pointer' }}
                            >
                                <FaArrowLeft size={16} className="me-2 text-muted" />
                                {!sidebarCollapsed && (
                                    <span className="text-muted small">{t('navigation.backToModules')}</span>
                                )}
                            </CardBody>
                        </Card>

                        {/* Module Menu Items with Submenu Support */}
                        {moduleConfig.menuItems?.map((item, idx) => (
                            item.subMenu ? (
                                <div key={item.label || idx}>
                                    <Card
                                        className={`mb-1 border-0 sidebar-menu-item ${openSubMenu === item.label ? 'active' : ''}`}
                                        onClick={() => setOpenSubMenu(openSubMenu === item.label ? null : item.label)}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <CardBody className="p-2 d-flex align-items-center justify-content-between">
                                            <div className="d-flex align-items-center">
                                                <item.icon size={16} className="me-2" style={{ color: moduleConfig.color }} />
                                                {!sidebarCollapsed && <span className="small">{item.label}</span>}
                                            </div>
                                            {!sidebarCollapsed && (
                                                openSubMenu === item.label ? <FaChevronDown size={12} /> : <FaChevronRight size={12} />
                                            )}
                                        </CardBody>
                                    </Card>
                                    {/* Submenu items */}
                                    {openSubMenu === item.label && (
                                        <div className="sidebar-submenu ms-4">
                                            {item.subMenu.map((subItem, subIdx) => (
                                                <Card
                                                    key={subItem.path || subIdx}
                                                    className={`mb-1 border-0 sidebar-menu-item ${location.pathname === subItem.path ? 'active' : ''}`}
                                                    onClick={() => navigate(subItem.path)}
                                                    style={{ cursor: 'pointer' }}
                                                >
                                                    <CardBody className="p-2 d-flex align-items-center">
                                                        <subItem.icon size={14} className="me-2" style={{ color: moduleConfig.color }} />
                                                        {!sidebarCollapsed && <span className="small">{subItem.label}</span>}
                                                    </CardBody>
                                                </Card>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ) : (
                                <Card
                                    key={item.path || idx}
                                    className={`mb-1 border-0 sidebar-menu-item ${location.pathname === item.path ? 'active' : ''}`}
                                    onClick={() => navigate(item.path)}
                                    style={{ cursor: 'pointer' }}
                                >
                                    <CardBody className="p-2 d-flex align-items-center">
                                        <item.icon size={16} className="me-2" style={{ color: moduleConfig.color }} />
                                        {!sidebarCollapsed && <span className="small">{item.label}</span>}
                                    </CardBody>
                                </Card>
                            )
                        ))}
                    </div>
                </aside>

                {/* Main Content Area */}
                <main className="module-main-content flex-grow-1">
                    <Container fluid className="p-4">
                        {children}
                    </Container>
                </main>
            </div>
        </div>
    );
};

export default ModuleLayout;
